import { FaLock, FaTrashAlt } from "react-icons/fa";
import { FaPlay, FaPause } from "react-icons/fa6";
import { IoClose, IoSchool, IoChevronForwardOutline } from "react-icons/io5";
import userPlaceholder from "../assets/images/users/user-vector.png";
import axios from "axios";
import { useState } from "react";
import {
  DELETE_USER,
  TOGGLE_PAUSE_ACCESS,
  TOGGLE_SCHOOL_MODE,
} from "../Routes";
import { generateURLOptions } from "../URLOptions";

const PeopleActionsModal = ({
  isOpen,
  onClose,
  person,
  onUpdate,
  onChangePassword,
}) => {
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  const toggleSchoolMode = async () => {
    try {
      const response = await axios.post(
        TOGGLE_SCHOOL_MODE,
        {
          userId: person?._id,
        },
        {
          headers: generateURLOptions(),
        },
      );

      console.log("School Mode Response: ", response.data);
      onClose();
      return onUpdate();
    } catch (error) {
      console.log("School Mode Error:", error);
      return alert(error);
    }
  };

  const togglePauseAccess = async () => {
    try {
      const response = await axios.post(
        TOGGLE_PAUSE_ACCESS,
        {
          userId: person?._id,
        },
        {
          headers: generateURLOptions(),
        },
      );

      console.log("Toggle Access Response: ", response.data);
      onClose();
      return onUpdate();
    } catch (error) {
      console.log("Toggle Access Error:", error);
      return alert(error);
    }
  };

  const deleteUser = async () => {
    try {
      const response = await axios.post(
        DELETE_USER,
        {
          userId: person?._id,
        },
        {
          headers: generateURLOptions(),
        },
      );

      console.log("Delete User Response: ", response.data);
      setShowDeleteWarning(false);
      onClose();
      return onUpdate();
    } catch (error) {
      console.log("Delete User Error:", error);
      setShowDeleteWarning(false);
      return alert(error);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteWarning(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteWarning(false);
  };

  return (
    <>
      {/* Main Modal - Overlay with fade transition */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[50] transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Modal Container with scale and fade */}
        <div
          className={`bg-white rounded-xl w-full max-w-sm mx-4 p-4 relative shadow-xl transition-all duration-300 ${
            isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <IoClose />
          </button>

          {/* Profile Header */}
          <div className="flex flex-row items-center">
            <img
              src={person?.profileImage ?? userPlaceholder}
              className="w-10 h-10 object-cover rounded-full"
              alt={person?.firstname ?? ""}
            />

            {/* User Profile Details */}
            <div className="flex flex-col ml-3">
              <h4 className="text-black font-medium text-base">
                {person?.firstname ?? ""} {person?.lastname ?? ""}
              </h4>

              <p className="text-black text-sm">{person?.email ?? ""}</p>
            </div>
          </div>

          {/* Menu Options */}
          <div
            onClick={toggleSchoolMode}
            className="cursor-pointer flex flex-row justify-start items-center mt-4 py-2 px-3 border border-slate-300 rounded-lg"
          >
            <IoSchool size={20} className="text-mpPurple" />

            <p className="text-sm font-medium text-black ml-2">
              {person?.schoolMode ? "Disable" : "Enable"} School Mode
            </p>

            <div style={{ flex: 1 }} />
            <IoChevronForwardOutline />
          </div>

          <div
            onClick={onChangePassword}
            className="cursor-pointer flex flex-row justify-start items-center mt-2 mb-2 py-2 px-3 border border-slate-300 rounded-lg"
          >
            <FaLock size={16} className="text-mpPurple" />

            <p className="text-sm font-medium text-black ml-3">
              Reset Password
            </p>

            <div style={{ flex: 1 }} />
            <IoChevronForwardOutline />
          </div>

          <div
            onClick={togglePauseAccess}
            className="cursor-pointer flex flex-row justify-start items-center mt-2 mb-2 py-2 px-3 border border-slate-300 rounded-lg"
          >
            {person?.status ? (
              <FaPause size={18} className="text-red-600" />
            ) : (
              <FaPlay size={18} className="text-green-700" />
            )}

            <p className="text-sm font-medium text-black ml-3">
              {person?.status ? "Pause Access" : "Enable Access"}
            </p>

            <div style={{ flex: 1 }} />
            <IoChevronForwardOutline />
          </div>

          <div
            onClick={handleDeleteClick}
            className="cursor-pointer flex flex-row justify-start items-center mt-2 mb-2 py-2 px-3 border border-slate-300 rounded-lg"
          >
            <FaTrashAlt size={18} className="text-red-600" />

            <p className="text-sm font-medium text-red-600 ml-3">Delete User</p>

            <div style={{ flex: 1 }} />
            <IoChevronForwardOutline />
          </div>
        </div>
      </div>

      {/* Delete Warning Dialog */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] transition-opacity duration-300 ${
          showDeleteWarning ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className={`bg-white rounded-xl w-full max-w-sm mx-4 p-6 shadow-xl transition-all duration-300 ${
            showDeleteWarning ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
        >
          {/* Warning Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <FaTrashAlt size={24} className="text-red-600" />
            </div>
          </div>

          {/* Warning Title */}
          <h3 className="text-xl font-semibold text-center text-gray-900 mb-2">
            Delete User?
          </h3>

          {/* Warning Message */}
          <p className="text-center text-gray-600 mb-6">
            Are you sure you want to delete{" "}
            <span className="font-semibold">
              {person?.firstname} {person?.lastname}
            </span>
            ? This action cannot be undone.
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleCancelDelete}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={deleteUser}
              className="flex-1 px-4 py-2 bg-red-600 rounded-lg text-white font-medium hover:bg-red-700 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PeopleActionsModal;
